// Special permissions as the mock-up draws them: grouped switches with a
// search box, All / None per group and a granted counter — instead of a bare
// column of checkboxes.
//
// The rows are the ones getweb/windowmenuspecialpermission already returns, and
// granting still means exactly what it meant before: userRole.id back to the
// role, revoking sets it to 0, and the save filters on that. Nothing about the
// payload changed.
//
// The only thing read out of the name is which group a switch belongs to: the
// till's "Disable…" / "…Disabled" switches take something away, so they are
// grouped apart, as the mock-up does, and nobody grants one by mistake.
import React, { useMemo, useRef, useState } from "react";
import { AxInfoIcon, AxSearchIcon } from "../ui/adminUi";

const isRestriction = (name) => /disable/i.test(String(name || ""));
const ALLOW = "Allowed actions";
const RESTRICT = "Restrictions";

const mark = (text, q) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

const SpecialPermissions = ({ items = [], onChange, loading, roleId, emptyText }) => {
  const [q, setQ] = useState("");
  const [grantedOnly, setGrantedOnly] = useState(false);
  // what each row's userRole.id was before it was switched off, so switching it
  // back on restores the same value the old screen restored
  const previous = useRef({});

  const needle = q.trim().toLowerCase();
  const granted = (row) => row?.userRole?.id !== 0;

  const setRow = (index, on) => {
    onChange(
      items.map((row, i) => {
        if (i !== index) return row;
        const current = row?.userRole?.id;
        if (on) {
          const restored = previous.current[index] || current || roleId || 1;
          return { ...row, userRole: { ...row.userRole, id: restored } };
        }
        previous.current[index] = current !== 0 ? current : previous.current[index] || roleId || 1;
        return { ...row, userRole: { ...row.userRole, id: 0 } };
      })
    );
  };

  const setGroup = (groupName, on) => {
    onChange(
      items.map((row, i) => {
        const g = isRestriction(row?.specialPermission?.name) ? RESTRICT : ALLOW;
        if (g !== groupName) return row;
        const current = row?.userRole?.id;
        if (on) {
          const restored = previous.current[i] || current || roleId || 1;
          return { ...row, userRole: { ...row.userRole, id: restored } };
        }
        previous.current[i] = current !== 0 ? current : previous.current[i] || roleId || 1;
        return { ...row, userRole: { ...row.userRole, id: 0 } };
      })
    );
  };

  const groups = useMemo(() => {
    const out = { [ALLOW]: [], [RESTRICT]: [] };
    (items || []).forEach((row, index) => {
      const name = row?.specialPermission?.name || "";
      if (needle && !name.toLowerCase().includes(needle)) return;
      if (grantedOnly && !granted(row)) return;
      out[isRestriction(name) ? RESTRICT : ALLOW].push({ row, index, name });
    });
    return out;
  }, [items, needle, grantedOnly]);

  const grantedCount = (items || []).filter(granted).length;
  const anyShown = groups[ALLOW].length + groups[RESTRICT].length > 0;

  return (
    <>
      <div className="axa-permbar">
        <div className="axa-psearch">
          <AxSearchIcon />
          <input
            type="text"
            placeholder="Find a permission…"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="axa-pbtn"
          disabled={!anyShown}
          onClick={() => {
            setGroup(ALLOW, true);
          }}
        >
          Allow all
        </button>
        <button
          type="button"
          className="axa-pbtn"
          disabled={!anyShown}
          onClick={() => {
            setGroup(ALLOW, false);
            setGroup(RESTRICT, false);
          }}
        >
          None
        </button>
        <button
          type="button"
          className="axa-pbtn"
          aria-pressed={grantedOnly}
          onClick={() => setGrantedOnly((v) => !v)}
        >
          Granted only
        </button>
        <span className="axa-pcount">
          <b>{grantedCount}</b> of {items?.length || 0} granted
        </span>
      </div>

      <div className="axa-special">
        {loading ? (
          <div className="axa-emptyperm">Loading permissions…</div>
        ) : !anyShown ? (
          <div className="axa-emptyperm">
            {items?.length ? "Nothing matches that filter." : emptyText || "No Data"}
          </div>
        ) : (
          <>
            {[ALLOW, RESTRICT].map((groupName) => {
              const rows = groups[groupName];
              if (!rows.length) return null;
              const on = rows.filter((r) => granted(r.row)).length;
              return (
                <div
                  className={"axa-spgroup" + (groupName === RESTRICT ? " restrict" : "")}
                  key={groupName}
                >
                  <div className="axa-sphead">
                    <b>{groupName}</b>
                    <span className="n">
                      {on} of {rows.length}
                    </span>
                    <span className="sp" />
                    <button type="button" onClick={() => setGroup(groupName, true)}>
                      All
                    </button>
                    <button type="button" onClick={() => setGroup(groupName, false)}>
                      None
                    </button>
                  </div>
                  <div className="axa-splist">
                    {rows.map(({ row, index, name }) => (
                      <label className="axa-spitem" key={row?.specialPermission?.id ?? index}>
                        <input
                          type="checkbox"
                          checked={granted(row)}
                          onChange={(e) => setRow(index, e.target.checked)}
                        />
                        <span className="t">
                          <b>
                            {mark(name, needle)}
                            {groupName === RESTRICT ? (
                              <span className="axa-restricts">restricts</span>
                            ) : null}
                          </b>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {groups[RESTRICT].length ? (
              <div className="axa-spnote">
                <AxInfoIcon />
                <span>
                  Ticking a <b>restricts</b> switch takes something away at the till. They are
                  grouped separately so nobody grants one by mistake.
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default SpecialPermissions;
